// journalNotifications.js - NEW FILE
import { LocalNotifications } from '@capacitor/local-notifications';
import { Capacitor } from '@capacitor/core';

// Notification message templates
const NOTIFICATION_MESSAGES = {
  // Same day reminders (8 hours after last entry)
  sameDay: [
    "Write about today before you forget, {name}? 📝",
    "Got anything to add to your journal, {name}? ✨",
    "Quick journal entry for today, {name}? 💭",
    "Want to write about what happened today, {name}? 📖",
    "Capture today's moments, {name}? 🌟"
  ],
  
  // 3-5 days since last entry
  fewDays: [
    "Haven't seen you journal in a bit, {name}. Catch up? 📚",
    "Want to write about your week, {name}? ✍️",
    "Time to update your journal, {name}? 📝",
    "Let's write down what's been going on, {name} 🌙",
    "Your journal's waiting, {name} 📖"
  ],
  
  // 2 weeks since last entry
  twoWeeks: [
    "It's been a while, {name}. Want to journal? 📚",
    "Time to write about these past weeks, {name}? ✨",
    "Let's catch up on your journal, {name} 📝",
    "Haven't written in a bit, {name}. Ready? 💫",
    "Your journal misses you, {name} 📖"
  ]
};

// Get a random message from a category
const getRandomMessage = (category, userName) => {
  const messages = NOTIFICATION_MESSAGES[category];
  const randomMsg = messages[Math.floor(Math.random() * messages.length)];
  return randomMsg.replace('{name}', userName);
};

// Request notification permission
export const requestJournalNotificationPermission = async () => {
  if (!Capacitor.isNativePlatform()) {
    console.log('📱 Notifications only work on mobile');
    return false;
  }
  
  try {
    const permission = await LocalNotifications.requestPermissions();
    const granted = permission.display === 'granted';
    
    if (granted) {
      console.log('✅ Journal notification permission granted');
      localStorage.setItem('journal_notifications_enabled', 'true');
    }
    
    return granted;
  } catch (error) {
    console.error('❌ Error requesting journal notification permission:', error);
    return false;
  }
};

// Check if notifications are enabled
export const areJournalNotificationsEnabled = () => {
  return localStorage.getItem('journal_notifications_enabled') === 'true';
};

// Cancel all journal notifications
export const cancelAllJournalNotifications = async () => {
  if (!Capacitor.isNativePlatform()) return;
  
  try {
    // Cancel all pending notifications with IDs 10000-10999 (journal range)
    const notificationIds = [10000, 10001, 10002];
    await LocalNotifications.cancel({ 
      notifications: notificationIds.map(id => ({ id })) 
    });
    console.log('🧹 All journal notifications cancelled');
  } catch (error) {
    console.error('❌ Error cancelling notifications:', error);
  }
};

// Schedule a notification
const scheduleNotification = async (id, title, body, scheduleDate) => {
  if (!Capacitor.isNativePlatform()) return;
  
  try {
    await LocalNotifications.schedule({
      notifications: [{
        id,
        title,
        body,
        schedule: { at: scheduleDate },
        sound: 'default',
        smallIcon: 'ic_notification',
        iconColor: '#667eea',
        extra: { type: 'journal_reminder' }
      }]
    });
    
    console.log(`📬 Journal notification scheduled for ${scheduleDate.toLocaleString()}`);
    return true;
  } catch (error) {
    console.error('❌ Error scheduling journal notification:', error);
    return false;
  }
};

// Main function: Update journal notifications based on last entry
export const updateJournalNotifications = async (entries, userName = 'there') => {
  if (!Capacitor.isNativePlatform() || !areJournalNotificationsEnabled()) {
    console.log('⏭️ Skipping journal notifications (not enabled or not mobile)');
    return;
  }
  
  // Cancel all existing journal notifications first
  await cancelAllJournalNotifications();
  
  if (!entries || entries.length === 0) {
    console.log('📝 No entries yet, no notifications needed');
    return;
  }
  
  // Get the most recent entry date
  const sortedEntries = [...entries].sort((a, b) => b.date - a.date);
  const lastEntryDate = new Date(sortedEntries[0].date);
  const now = new Date();
  
  // Calculate days since last entry
  const daysSinceLastEntry = Math.floor((now - lastEntryDate) / (1000 * 60 * 60 * 24));
  
  console.log(`📊 Last journal entry: ${daysSinceLastEntry} days ago`);
  
  // Check if user already journaled today
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const hasJournaledToday = lastEntryDate >= todayStart;
  
  if (hasJournaledToday) {
    // Schedule 8-hour reminder (ONLY ONCE, same day)
    const eightHoursLater = new Date(lastEntryDate.getTime() + (8 * 60 * 60 * 1000));
    
    // Only schedule if it's still today and in the future
    const isStillToday = eightHoursLater.getDate() === now.getDate();
    const isInFuture = eightHoursLater > now;
    
    if (isStillToday && isInFuture) {
      const message = getRandomMessage('sameDay', userName);
      await scheduleNotification(
        10000, // ID for same-day reminder
        'Journal Check-in',
        message,
        eightHoursLater
      );
      console.log('✅ 8-hour same-day reminder scheduled');
    } else {
      console.log('⏭️ Skipping 8-hour reminder (would be tomorrow or in past)');
    }
    
  } else if (daysSinceLastEntry >= 3 && daysSinceLastEntry < 14) {
    // 3-5 days: Schedule next reminder for tomorrow at 6 PM
    const tomorrow6PM = new Date(now);
    tomorrow6PM.setDate(tomorrow6PM.getDate() + 1);
    tomorrow6PM.setHours(18, 0, 0, 0);
    
    const message = getRandomMessage('fewDays', userName);
    await scheduleNotification(
      10001, // ID for few-days reminder
      'Your Journal Awaits',
      message,
      tomorrow6PM
    );
    console.log('✅ Few-days reminder scheduled for tomorrow 6 PM');
    
  } else if (daysSinceLastEntry >= 14) {
    // 2+ weeks: Schedule reminder for tomorrow at 7 PM
    const tomorrow7PM = new Date(now);
    tomorrow7PM.setDate(tomorrow7PM.getDate() + 1);
    tomorrow7PM.setHours(19, 0, 0, 0);
    
    const message = getRandomMessage('twoWeeks', userName);
    await scheduleNotification(
      10002, // ID for two-weeks reminder
      'Jouranl Check-in',
      message,
      tomorrow7PM
    );
    console.log('✅ Two-weeks reminder scheduled for tomorrow 7 PM');
  }
  
  // Save last check timestamp
  localStorage.setItem('journal_last_notification_check', now.toISOString());
};

// Disable journal notifications
export const disableJournalNotifications = async () => {
  localStorage.setItem('journal_notifications_enabled', 'false');
  await cancelAllJournalNotifications();
  console.log('🔕 Journal notifications disabled');
};

// Enable journal notifications
export const enableJournalNotifications = async () => {
  const granted = await requestJournalNotificationPermission();
  if (granted) {
    console.log('🔔 Journal notifications enabled');
  }
  return granted;
};